/**
 * Login Form Component
 *
 * Simple authentication form for user login:
 * - Email input with format validation
 * - Password input field
 * - Form submission with error handling
 * - Displays authentication errors to user
 */

import * as React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Alert,
  Button,
  CircularProgress,
  Grid,
  TextField,
} from "@mui/material";

interface LoginFormProps {
  onSubmit: (email: string, password: string) => void;
  submissionError: string;
  isSubmitting: boolean;
}

const LoginForm: React.FC<LoginFormProps> = ({
  onSubmit,
  submissionError,
  isSubmitting,
}) => {
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      password: Yup.string().required("Password is required"),
    }),
    onSubmit: (values) => {
      const { email, password } = values;

      onSubmit(email, password);
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            id="email"
            name="email"
            label="Email"
            variant="outlined"
            autoComplete="email"
            disabled={isSubmitting}
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            id="password"
            name="password"
            label="Password"
            type="password"
            variant="outlined"
            autoComplete="current-password"
            disabled={isSubmitting}
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? (
                <CircularProgress color="inherit" size={16} />
              ) : undefined
            }
            sx={{
              borderRadius: 20,
              fontWeight: 700,
              boxShadow: "0 0 18px rgba(255, 255, 255, 0.0)",
              "&:hover": {
                boxShadow: "0 0 22px rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            {isSubmitting ? "Signing in..." : "Log In"}
          </Button>
        </Grid>
        {submissionError && (
          <Grid size={{ xs: 12 }}>
            <Alert variant="filled" severity="error">
              {submissionError}
            </Alert>
          </Grid>
        )}
      </Grid>
    </form>
  );
};

export default LoginForm;
